import moment from 'moment';
import { Fragment, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	MANAGEMENT_ROLE_ID,
	MANAGEMENT_ROLE_TYPES,
	type ManagementRoleType,
} from '@treams/treams-analytics';
import { useWorkspaceConfiguration } from '@treams/treams-hooks';
import { type Group, GroupType } from '@treams/treams-network';
import {
	Container,
	DateIntervalNavigation,
	type ITimeMachine,
	Label,
	type Option,
	Select,
	Separator,
	tokens,
} from '@treams/treams-ui';

import type {
	GroupSelection,
	SelectableGroupFilter,
} from '../../goals/group-selector/new/GroupSelector.types';
import { GroupSelectorNew } from '../../goals/group-selector/new/GroupSelectorNew';

const StickyFilterHeader = styled(Box)`
	position: sticky;
	top: 0;
	background-color: ${tokens.surface.white};
	z-index: 2;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

/*
 * The subset of `useGroupSelection` (and its look-alikes) that the department
 * filter needs to render. Declared structurally so any hook producing this
 * shape can drive the filter.
 */
export type AnalyticsGroupSelection = {
	allGroups: Group[];
	groupSelection: GroupSelection[];
	groupsInSelection: Group[];
	hasExplicitGroupSelection: boolean;
};

type AnalyticsFilterProps = {
	cycleTemplateID?: string;
	cycleTemplateOptions?: Option[];
	onCycleTemplateChange?: (cycleTemplateID: string) => void;

	talentReviewTemplateID?: string;
	talentReviewTemplateOptions?: Option[];
	onTalentReviewTemplateChange?: (talentReviewTemplateID: string) => void;

	year?: number;
	years?: number[];
	onYearChange?: (year: number) => void;

	roleID?: string;
	onRoleChange?: (roleID: string | undefined) => void;

	departments?: AnalyticsGroupSelection;
	canSelectGroup?: SelectableGroupFilter;
	onDepartmentChange?: (selection: GroupSelection[]) => void;

	timeMachine?: ITimeMachine;
	allowFutureDates?: boolean;
	disableQuery?: boolean;
	onTimeMachineChange?: (timeMachine: ITimeMachine) => void;
};

export const canSelectDepartment: SelectableGroupFilter = (group) =>
	group.type === GroupType.Department;

function Field(props: { label: string; children: ReactNode }) {
	return (
		<Box>
			<Label mb={2}>{props.label}</Label>
			{props.children}
		</Box>
	);
}

/*
 * The filter bar shared by the analytics pages. Every filter is optional and is
 * rendered only when its change handler is passed, in a fixed left-to-right
 * order so the pages stay visually consistent. The period navigation is always
 * pinned to the right.
 */
export function AnalyticsFilter(props: AnalyticsFilterProps) {
	const {
		onCycleTemplateChange,
		onTalentReviewTemplateChange,
		onYearChange,
		onRoleChange,
		onDepartmentChange,
		onTimeMachineChange,
	} = props;

	const { t } = useTranslation();
	const workspaceConfiguration = useWorkspaceConfiguration();

	const roleOptions = useMemo(
		() =>
			MANAGEMENT_ROLE_TYPES.map((type: ManagementRoleType) => ({
				title: t(`roles.${MANAGEMENT_ROLE_ID[type]}`),
				value: MANAGEMENT_ROLE_ID[type],
			})),
		[t],
	);

	const yearOptions = useMemo(() => {
		const years = props.years?.length ? props.years : [new Date().getFullYear()];

		return years.map((year) => ({ title: String(year), value: String(year) }));
	}, [props.years]);

	const fields: { key: string; node: ReactNode }[] = [];

	if (onCycleTemplateChange) {
		fields.push({
			key: 'cycleTemplate',
			node: (
				<Field label={t('analytics.cycles.cycleTemplate')}>
					{props.cycleTemplateID ? (
						<Select
							selectedValue={props.cycleTemplateID}
							options={props.cycleTemplateOptions}
							onChange={(value) => value && onCycleTemplateChange(value)}
						/>
					) : (
						<Select.Skeleton />
					)}
				</Field>
			),
		});
	}

	if (onTalentReviewTemplateChange) {
		fields.push({
			key: 'talentReviewTemplate',
			node: (
				<Field label={t('talentReviews.create.templateLabel')}>
					{props.talentReviewTemplateID ? (
						<Select
							selectedValue={props.talentReviewTemplateID}
							options={props.talentReviewTemplateOptions}
							onChange={(value) => value && onTalentReviewTemplateChange(value)}
						/>
					) : (
						<Select.Skeleton />
					)}
				</Field>
			),
		});
	}

	if (onYearChange) {
		fields.push({
			key: 'year',
			node: (
				<Field label={t('analytics.cycles.year')}>
					<Select
						selectedValue={props.year === undefined ? undefined : String(props.year)}
						selectedOption={
							props.year === undefined
								? undefined
								: { title: String(props.year), value: String(props.year) }
						}
						options={yearOptions}
						onChange={(value) => value && onYearChange(Number(value))}
					/>
				</Field>
			),
		});
	}

	if (onRoleChange) {
		fields.push({
			key: 'role',
			node: (
				<Field label={t('analytics.cycles.filterRoles')}>
					<Select
						search={false}
						clearable
						placeholder={t('common.all')}
						selectedValue={props.roleID ?? ''}
						options={roleOptions}
						onChange={(value) => onRoleChange(value || undefined)}
					/>
				</Field>
			),
		});
	}

	if (onDepartmentChange && props.departments) {
		const departments = props.departments;

		fields.push({
			key: 'department',
			node: (
				<Field label={t('analytics.cycles.group')}>
					<GroupSelectorNew
						canSelect={props.canSelectGroup ?? canSelectDepartment}
						onChange={onDepartmentChange}
						allGroups={departments.allGroups}
						groupSelection={departments.groupSelection}
						hasExplicitSelection={departments.hasExplicitGroupSelection}
						groupsInSelection={departments.groupsInSelection}
					/>
				</Field>
			),
		});
	}

	const timeMachine = props.timeMachine;

	return (
		<StickyFilterHeader>
			<Container py={3} wide={true}>
				<Flex alignItems="center" flexWrap="wrap" style={{ rowGap: tokens.gap.list }}>
					{fields.map((field, index) => (
						<Fragment key={field.key}>
							{index > 0 && <Separator vertical mx={4} />}
							{field.node}
						</Fragment>
					))}

					{onTimeMachineChange && timeMachine && (
						<Box ml="auto" flexShrink={0}>
							<Field label={t('analytics.treamsScore.timePeriod')}>
								{workspaceConfiguration.loading ? (
									<DateIntervalNavigation.Skeleton />
								) : (
									<DateIntervalNavigation
										value={timeMachine}
										onChange={onTimeMachineChange}
										allowFutureDates={props.allowFutureDates}
										disableQuery={props.disableQuery}
										workspaceStartDate={
											workspaceConfiguration.data?.startDate
												? moment(workspaceConfiguration.data.startDate).toDate()
												: undefined
										}
									/>
								)}
							</Field>
						</Box>
					)}
				</Flex>
			</Container>
		</StickyFilterHeader>
	);
}
